import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Sparkles,
  Tag,
  Copy,
  Check,
  Calendar,
  ArrowRight,
  Layers,
  ShoppingBag,
} from "lucide-react";

const getApiBase = () => {
  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  ) {
    return "http://localhost:5000";
  }
  return (
    process.env.REACT_APP_API_BASE_URL ||
    "https://api.vanyacollectionsrajahmundry.com"
  );
};
const API_BASE = getApiBase();

const SaleOfTheDayPopup = () => {
  const navigate = useNavigate();
  const [popupData, setPopupData] = useState(null);
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // If popup was already dismissed during this browser session/load, do not show again!
    if (window.__vanyaSalePopupDismissed) {
      return;
    }

    let isMounted = true;

    const fetchActiveSalePopup = async () => {
      try {
        const res = await fetch(`${API_BASE}/cart/sale-popup/active`);
        if (!res.ok) return;
        const data = await res.json();

        if (isMounted && data.success && data.popup) {
          // Double check expiry on client side as an extra safeguard
          if (data.popup.end_date) {
            const expDate = new Date(data.popup.end_date);
            expDate.setHours(23, 59, 59, 999);
            if (expDate < new Date()) {
              return; // Expired, do not show
            }
          }

          setPopupData(data.popup);

          // Delay opening slightly after page load for smooth animation
          const timer = setTimeout(() => {
            if (isMounted && !window.__vanyaSalePopupDismissed) {
              setVisible(true);
            }
          }, 1200);

          return () => clearTimeout(timer);
        }
      } catch (err) {
        console.warn("Could not load sale popup:", err);
      }
    };

    fetchActiveSalePopup();

    return () => {
      isMounted = false;
    };
  }, []);

  // Automatically disappear after 10 seconds if user does not click close
  useEffect(() => {
    if (!visible) return;

    const timer = setTimeout(() => {
      handleClose();
    }, 10000);

    return () => clearTimeout(timer);
  }, [visible]);

  const handleClose = () => {
    setVisible(false);
    // Mark as dismissed so that subsequent route changes (shop, cart, payment, etc.) will NEVER show it again
    window.__vanyaSalePopupDismissed = true;
  };

  const handleCopyCoupon = (code) => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const handleCtaClick = () => {
    handleClose();
    if (popupData?.cta_link) {
      navigate(popupData.cta_link);
    } else {
      navigate("/shop");
    }
  };

  if (!visible || !popupData) {
    return null;
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="vanya-sale-popup-overlay" onClick={handleClose}>
      <style>{`
        .vanya-sale-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(22, 10, 31, 0.72);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          z-index: 999999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          box-sizing: border-box;
          animation: vanyaFadeIn 0.3s ease-out forwards;
        }

        .vanya-sale-modal {
          background: radial-gradient(circle at 50% 0%, #582b6b 0%, #361445 100%);
          border: 2px solid #D4AF37;
          border-radius: 28px;
          width: 100%;
          max-width: 520px;
          position: relative;
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.5), 0 0 50px rgba(212, 175, 55, 0.25);
          color: #ffffff;
          padding: 38px 32px 34px;
          text-align: center;
          box-sizing: border-box;
          overflow: hidden;
          animation: vanyaScaleUp 0.38s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
          font-family: 'Playfair Display', serif;
        }

        .vanya-sale-modal::before {
          content: "";
          position: absolute;
          top: -60px;
          left: 50%;
          transform: translateX(-50%);
          width: 280px;
          height: 140px;
          background: radial-gradient(circle, rgba(212, 175, 55, 0.25) 0%, transparent 70%);
          pointer-events: none;
        }

        .vanya-popup-close-btn {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.12);
          border: 1px solid rgba(212, 175, 55, 0.4);
          color: #f3e5ab;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.25s ease;
          z-index: 10;
        }

        .vanya-popup-close-btn:hover {
          background: #D4AF37;
          color: #361445;
          transform: rotate(90deg) scale(1.08);
        }

        /* Top Royal Header Pill */
        .vanya-royal-tag {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(212, 175, 55, 0.15);
          border: 1px solid rgba(212, 175, 55, 0.45);
          color: #fbd349;
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          padding: 6px 16px;
          border-radius: 30px;
          margin-bottom: 16px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Title & Tagline */
        .vanya-popup-title {
          font-size: 2.1rem;
          color: #ffffff;
          font-weight: 800;
          margin: 0 0 6px;
          line-height: 1.25;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
        }

        .vanya-popup-tagline {
          font-size: 0.95rem;
          color: #f3e5ab;
          margin: 0 0 22px;
          letter-spacing: 0.5px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Big Offer Badge Box */
        .vanya-discount-display {
          display: inline-block;
          background: linear-gradient(135deg, #d4af37 0%, #aa8c2c 100%);
          color: #2e0f3c;
          padding: 10px 24px;
          border-radius: 40px;
          font-size: 1.55rem;
          font-weight: 900;
          letter-spacing: 1px;
          margin-bottom: 16px;
          box-shadow: 0 6px 25px rgba(212, 175, 55, 0.45);
          border: 1px solid #ffe898;
        }

        /* Scope & Category Pill */
        .vanya-scope-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(0, 0, 0, 0.25);
          border: 1px solid rgba(212, 175, 55, 0.3);
          color: #e2f1ec;
          font-size: 13px;
          font-weight: 600;
          padding: 5px 14px;
          border-radius: 20px;
          margin-bottom: 18px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Description Body */
        .vanya-popup-desc {
          color: #f5edf7;
          font-size: 14.5px;
          line-height: 1.6;
          margin: 0 auto 22px;
          max-width: 440px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Coupon Box */
        .vanya-coupon-strip {
          background: rgba(255, 255, 255, 0.08);
          border: 1.5px dashed rgba(212, 175, 55, 0.65);
          border-radius: 14px;
          padding: 12px 18px;
          margin-bottom: 22px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .vanya-coupon-left {
          text-align: left;
        }

        .vanya-coupon-label {
          font-size: 11px;
          text-transform: uppercase;
          color: #f3e5ab;
          letter-spacing: 1px;
          font-weight: 700;
        }

        .vanya-coupon-code {
          font-size: 1.15rem;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: 1.5px;
          font-family: monospace;
          margin-top: 2px;
        }

        .vanya-coupon-copy-btn {
          background: #D4AF37;
          color: #361445;
          border: none;
          padding: 8px 14px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 800;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .vanya-coupon-copy-btn:hover {
          background: #e2be42;
          transform: translateY(-1px);
        }

        /* Date validity info */
        .vanya-validity-note {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 12px;
          color: #f3e5ab;
          margin-bottom: 22px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* CTA Button */
        .vanya-popup-cta {
          width: 100%;
          background: linear-gradient(135deg, #d4af37 0%, #aa8c2c 100%);
          color: #361445;
          border: none;
          border-radius: 35px;
          padding: 16px;
          font-size: 16px;
          font-weight: 800;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.25s ease;
          box-shadow: 0 8px 25px rgba(212, 175, 55, 0.4);
          font-family: 'Playfair Display', serif;
          letter-spacing: 0.5px;
        }

        .vanya-popup-cta:hover {
          background: linear-gradient(135deg, #e5be42, #b89832);
          transform: translateY(-2px);
          box-shadow: 0 12px 30px rgba(212, 175, 55, 0.55);
        }

        .vanya-popup-dismiss-text {
          margin-top: 14px;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          text-decoration: underline;
          background: none;
          border: none;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .vanya-popup-dismiss-text:hover {
          color: #ffffff;
        }

        @keyframes vanyaFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes vanyaScaleUp {
          from {
            opacity: 0;
            transform: scale(0.85) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @media (max-width: 600px) {
          .vanya-sale-modal {
            padding: 30px 20px 24px;
            border-radius: 22px;
          }
          .vanya-popup-title {
            font-size: 1.65rem;
          }
          .vanya-discount-display {
            font-size: 1.3rem;
            padding: 8px 20px;
          }
        }
      `}</style>

      <div
        className="vanya-sale-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* CLOSE BUTTON */}
        <button
          className="vanya-popup-close-btn"
          onClick={handleClose}
          title="Close Offer"
        >
          <X size={18} />
        </button>

        {/* TOP ROYAL PILL */}
        <div className="vanya-royal-tag">
          <Sparkles size={14} color="#fbd349" />
          <span>SALE FOR THE DAY</span>
        </div>

        {/* HEADLINE */}
        <h2 className="vanya-popup-title">{popupData.title}</h2>

        {/* SUBTITLE / TAGLINE */}
        {popupData.tagline && (
          <p className="vanya-popup-tagline">{popupData.tagline}</p>
        )}

        {/* DISCOUNT BADGE */}
        {popupData.offer_badge && (
          <div>
            <div className="vanya-discount-display">
              {popupData.offer_badge}
            </div>
          </div>
        )}

        {/* SCOPE & CONDITION */}
        {popupData.apply_type === "category" && popupData.category_name ? (
          <div>
            <span className="vanya-scope-chip">
              <Layers size={14} color="#fbd349" />
              Special Offer on {popupData.category_name} Sarees
            </span>
          </div>
        ) : popupData.apply_type === "amount" && Number(popupData.min_amount) > 0 ? (
          <div>
            <span className="vanya-scope-chip">
              <ShoppingBag size={14} color="#fbd349" />
              Applicable on purchases above ₹{Number(popupData.min_amount).toLocaleString("en-IN")}
            </span>
          </div>
        ) : (
          <div>
            <span className="vanya-scope-chip">
              <Sparkles size={14} color="#fbd349" />
              Valid on All Royal Saree Collections
            </span>
          </div>
        )}

        {/* DESCRIPTION MATTER */}
        {popupData.description && (
          <p className="vanya-popup-desc">{popupData.description}</p>
        )}

        {/* COUPON CODE STRIP (IF PROVIDED) */}
        {popupData.coupon_code && (
          <div className="vanya-coupon-strip">
            <div className="vanya-coupon-left">
              <div className="vanya-coupon-label">Promo Code</div>
              <div className="vanya-coupon-code">{popupData.coupon_code}</div>
            </div>
            <button
              className="vanya-coupon-copy-btn"
              onClick={() => handleCopyCoupon(popupData.coupon_code)}
            >
              {copied ? (
                <>
                  <Check size={14} />
                  <span>COPIED!</span>
                </>
              ) : (
                <>
                  <Copy size={14} />
                  <span>COPY CODE</span>
                </>
              )}
            </button>
          </div>
        )}


        {/* PRIMARY CTA BUTTON */}
        <button className="vanya-popup-cta" onClick={handleCtaClick}>
          <span>{popupData.cta_text || "Shop Royal Sarees Now"}</span>
          <ArrowRight size={18} />
        </button>

        <div>
          <button className="vanya-popup-dismiss-text" onClick={handleClose}>
            No thanks, continue browsing
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaleOfTheDayPopup;
